import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  ALSO_OWNS_PILL,
  REPLIES_ANCHOR,
  type ReplyPreviewView,
  buyAgainChip,
  foundingTag,
  helpfulCount,
  productHubHint,
  productHubHref,
  readChips,
  replyCount,
  replyCountLine,
  replyPreviewModel,
  reviewHref,
  safeImageUrl,
  sentCount,
  sgScoreRing,
  shortAge,
  voteCounts,
} from 'lib/community/model'
import { knownHandles } from 'lib/community/mentions'
import { shownReplyCount, useOwnReplyTotal } from 'lib/community/own-reply'
import type {
  CommunityFeedItem,
  CommunityHelpfulAnswer,
  CommunityPoints,
  FeedControlAnalytics,
} from 'lib/community/types'
import AskOwner from './AskOwner'
import { MaybeLink } from './Crumbs'
import ReplyRuns from './discussions/ReplyRuns'
import TeamPill from './discussions/TeamPill'
import FeedQuote from './FeedQuote'
import ReviewPhotos from 'components/owner-notes/ReviewPhotos'
import HelpfulButton from './HelpfulButton'
import { MemberCardTrigger, isMemberAuthor } from './MemberCard'
import MemberName from './MemberName'
import SgScoreRing from './SgScoreRing'
import TierAvatar from './TierAvatar'
import VerifiedCheck from './VerifiedCheck'
import VoteControl from './VoteControl'
import base from './community.module.css'
import styles from './Feed.module.css'

interface FeedRowProps {
  item: CommunityFeedItem
  /** The page's timestamp — ages are printed against it so server and client agree. */
  now: number
  /** On a member's own page the author is the page: the row drops the author. */
  showAuthor?: boolean
  /** On a product's hub the product is the page: the row drops the product (thumb, name, score
   *  ring). The by-product view's minis (plan slice 5) reuse the row the same way. */
  showProduct?: boolean
  /** A condensed row (feed plan slice 5, the By product groups — mockup board 2): implies
   *  `showProduct={false}`; the author line without the "Owner review" kicker or the Founding
   *  tag, the sentence on one ellipsised line, the reads, the vote line and its Ask — no photos,
   *  no reply preview, no reply count (the group head's "All N →" leads to the threads). */
  condensed?: boolean
  /** The list's memory of Helpful answers (`CommunityFeed`), so a remounted row keeps the vote. */
  helpfulMemory?: Map<string, CommunityHelpfulAnswer>
  /** The list's batched read of the reader's votes (`useHelpfulMine`), by note id. */
  helpfulMine?: ReadonlyMap<string, CommunityHelpfulAnswer>
  /** No longer read (2026-10-18: the row's "Reply first" and its "+10" went). Kept only so the
   *  callers that still pass it (CommunityFeed, ProductIndex) compile — drop it with them. */
  points?: CommunityPoints | null
  /** The row's `id` — a deep-link target (`noteAnchorId`: "#note-<id>", the member's Gear tab). */
  anchorId?: string
  /** Lit for a moment after a deep link landed on it. */
  highlighted?: boolean
  /**
   * The member's own row on their Gear tab (design 1b, hydrated only — never in the HTML): the
   * private meta line ("312 readers · 4 Helpful · Updated Sep 11") replaces the age and the
   * Helpful control (a member cannot vote on their own review), `lines` sit under the sentence,
   * `aside` is the right column (status, edit, the 6-month hint) — under the body on a phone.
   */
  owner?: FeedRowOwner
  /** The vote's and the Ask's analytics `page_type` / `location`: the feed's by default; a best
   *  list's Owners block (`PickOwners`) passes `guide_page` and its surface. */
  analytics?: FeedControlAnalytics
}

export interface FeedRowOwner {
  meta: string | null
  lines: React.ReactNode
  aside: React.ReactNode
}

/** The ✓ / ✕ glyph of a read, and the words a screen reader hears in its place. */
const READ_GLYPH = {
  better: { glyph: '✓', words: 'Better than expected: ' },
  worse: { glyph: '✕', words: 'Worse than expected: ' },
} as const

/**
 * One reply under the reads (feed plan §2 "Reply preview", contract #210): the replier's avatar
 * and name, their pill (SOUNDGUYS TEAM, else "Also owns it"), the age, then the text on at most
 * two lines with "N replies →" to the thread at its end. The text is the excerpt DealHunt cut;
 * its links and mentions read as on a discussion answer (`ReplyRuns`), without link cards. All of
 * it is public — the shared-cached HTML carries it for every reader.
 */
const FeedReplyPreview: React.VFC<{
  view: ReplyPreviewView
  createdAt: string
  /** The review's author's handle when they are a member (a public page to link), else null. */
  authorHandle: string | null
}> = ({ view, createdAt, authorHandle }) => (
  <div className={styles.replyPreview}>
    <p className={clsx(styles.meta, styles.replyMeta)}>
      {/* The name follows: the disc says nothing more ("Owner" would be wrong for the team). */}
      <span className={styles.replyAvatar} aria-hidden="true">
        <TierAvatar
          handle={view.avatarKey}
          tier={null}
          size="xs"
          src={view.avatarUrl}
          name={view.name}
        />
      </span>
      <span className={styles.authorPlain}>
        {view.handle ? <MemberName name={view.name} handle={view.handle} nameOnly /> : view.name}
      </span>
      {view.pill === 'team' && <TeamPill size="answer" />}
      {view.pill === 'owns' && <span className={styles.ownsPill}>{ALSO_OWNS_PILL}</span>}
      {view.age && (
        <time className={styles.age} dateTime={createdAt}>
          {`· ${view.age}`}
        </time>
      )}
    </p>
    {/* The link floats to the end of the last visible line (`.replyText`), so a clamped text
      never hides it; it comes first in the markup for the float to work. */}
    <div className={styles.replyTextWrap}>
      <p className={styles.replyText}>
        <MaybeLink href={view.href} className={styles.replyMore}>
          {view.repliesLabel}
        </MaybeLink>
        <ReplyRuns line={view.text} known={knownHandles(null, null, [authorHandle, view.handle])} />
      </p>
    </div>
  </div>
)

/**
 * One owner review (code name: owner note) in a community list (design 8a; feed plan slice 1):
 * the product leads the card — its image and its name, both to the product's threads page
 * (`productHubHref`), "N owner reviews →" beside the name once DealHunt sends the count, the
 * SoundGuys rating ring under the image when DealHunt sends the score — then the author line
 * (with the owner's buy-again pill: a verdict, never a read), the sentence (to the review's
 * thread), the reads line (✓ / ✕, no headings; no list legend since 2026-10-18 — each row's visually-hidden read label is the key), one reply
 * under it when the thread has one (slice 3, `FeedReplyPreview` — it then carries the reply
 * count, so the action row does not repeat it), the vote
 * line — "Did this help you decide?" Yes / No (slice 2, `VoteControl`: the public counts in the
 * HTML, the reader's own vote after hydration, signed-out readers vote with the device) with
 * "Ask <handle>" at its right end and the chips under it (slice 4, `AskOwner`: closed in the
 * HTML, the item's `asked` links in it; on its own line when there is no vote) — and the
 * action row: the reply count once someone has replied (the reader's own reply on this browser
 * counts at once, after mount — `lib/community/own-reply`); nothing while nobody has (2026-10-18:
 * no "Reply first" — the thread's title and sentence are the way in). Each element appears only when DealHunt sends
 * its figure: without `vote_counts` (a DealHunt before the vote's value, contract #199 — a "No"
 * would be stored as a Helpful there) the action row keeps the Helpful button, and an older
 * DealHunt still renders the phase-1 row.
 *
 * Views: the row shows NO reader count. Counts are for signed-in readers only (#155) and this
 * HTML is shared-cached. Reply and Helpful counts are not reader counts.
 */
const FeedRow: React.VFC<FeedRowProps> = ({
  item,
  now,
  showAuthor = true,
  showProduct: showProductProp = true,
  condensed = false,
  helpfulMemory,
  helpfulMine,
  anchorId,
  highlighted = false,
  owner,
  analytics,
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const showProduct = showProductProp && !condensed
  const { note, product, author, labels } = item
  const chips = readChips(item)
  const again = buyAgainChip(item)
  const age = owner ? '' : shortAge(note.published_at, now)
  // DealHunt's count, raised after mount to the reader's own reply on this browser (DealHunt trails
  // commentHub by minutes) — "N replies" shows once they have replied.
  const ownReplies = useOwnReplyTotal(note.id)
  const replies = shownReplyCount(replyCount(item), ownReplies)
  const preview = condensed ? null : replyPreviewModel(item, now, trailingSlash)
  // Only the preview's count is raised — the previewed reply itself stays DealHunt's.
  const previewCount = shownReplyCount(sentCount(item.reply_preview?.reply_count), ownReplies)
  const replyPreview =
    preview && ownReplies !== null && previewCount !== null
      ? { ...preview, repliesLabel: `${replyCountLine(previewCount)} →` }
      : preview
  // The preview's "N replies →" is the row's count; without a preview the action row says it.
  const repliesInActions = replies !== null && replies > 0 && !replyPreview && !condensed
  const helpful = owner ? null : helpfulCount(item)
  const votes = helpful === null ? null : voteCounts(item)
  const thread = reviewHref(item, trailingSlash)
  const hub = product.slug ? productHubHref(product.slug, trailingSlash) : null
  const hubHint = hub ? productHubHint(product) : null
  const image = showProduct ? safeImageUrl(product.image_url) : null
  const score = showProduct ? sgScoreRing(product) : null
  const founding = author.member?.founding_number

  const thumbImage = image ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={image} alt="" loading="lazy" />
  ) : null

  const body = (
    <div className={styles.body}>
      {showProduct && (
        <p className={styles.titleLine}>
          <MaybeLink href={hub} className={styles.title}>
            {product.name}
          </MaybeLink>
          {product.category && <span className={styles.category}>{product.category.name}</span>}
          {hub && hubHint && (
            <Linked href={hub} className={styles.hubHint}>
              {hubHint}
            </Linked>
          )}
        </p>
      )}
      <p className={styles.meta}>
        {!showProduct && !condensed && <span className={base.kind}>{'Owner review'}</span>}
        {showAuthor &&
          (isMemberAuthor(author) ? (
            // One flex item: the check stays glued to the handle, outside the card's trigger
            // (its own popover never opens with the card) and outside the link (a button).
            <span className={styles.authorWho}>
              <MemberCardTrigger author={author} linkClassName={styles.author} nameOnly />
              <VerifiedCheck />
            </span>
          ) : (
            // The same one item for an anonymous author (avatar + handle), so the phone's byline
            // gives the author its own line in both cases (`.mobileCard .authorWho`).
            <span className={styles.authorWho}>
              <TierAvatar
                handle={author.handle}
                tier={null}
                size="xs"
                src={author.avatar_url}
                name={author.display_name}
              />
              <span className={styles.authorPlain}>
                <MemberName name={author.display_name} handle={author.handle} nameOnly />
              </span>
            </span>
          ))}
        {labels.ownership_length && (
          <span className={clsx(base.ownerPill, styles.metaOwner)}>
            {`Owner · ${labels.ownership_length}`}
          </span>
        )}
        {showAuthor && !condensed && typeof founding === 'number' && (
          <span className={clsx(base.founding, styles.metaFounding)}>{foundingTag(founding)}</span>
        )}
        <span className={clsx(styles.buyPill, again.yes ? styles.buyPill_yes : styles.buyPill_no)}>
          <span aria-hidden="true">{'↻ '}</span>
          {again.text}
        </span>
        {age && (
          <time className={styles.age} dateTime={note.published_at}>
            {`· ${age}`}
          </time>
        )}
      </p>
      {/* The owner's photos lead the sentence, large (2026-10-19: readers go to the picture
          before the text); the phone grid gives them their own track (`.photos`). */}
      {!condensed && (
        <ReviewPhotos
          note={note}
          productName={product.name}
          size="large"
          className={styles.photos}
        />
      )}
      <FeedQuote href={thread} text={note.note} oneLine={condensed} />
      {owner?.lines}
      {chips.length > 0 && (
        <ul className={styles.reads}>
          {chips.map(chip => (
            <li key={chip.id} className={clsx(styles.read, styles[`read_${chip.tone}`])}>
              <span className={styles.readGlyph} aria-hidden="true">
                {READ_GLYPH[chip.tone].glyph}
              </span>
              <span className={styles.srOnly}>{READ_GLYPH[chip.tone].words}</span>
              {chip.label}
            </li>
          ))}
        </ul>
      )}
      {replyPreview && item.reply_preview && (
        <FeedReplyPreview
          view={replyPreview}
          createdAt={item.reply_preview.created_at}
          authorHandle={author.member ? author.handle : null}
        />
      )}
      {owner?.meta && <p className={styles.ownMeta}>{owner.meta}</p>}
      {owner ? (
        votes && (
          <VoteControl
            noteId={note.id}
            handle={author.handle}
            counts={votes}
            memory={helpfulMemory}
            known={helpfulMine?.get(note.id)}
            product={product}
            className={styles.voteLine}
            analytics={analytics}
          />
        )
      ) : (
        <AskOwner item={item} panelClassName={styles.askPanel} analytics={analytics}>
          {({ button, panel }) => (
            <>
              {votes ? (
                <VoteControl
                  noteId={note.id}
                  handle={author.handle}
                  counts={votes}
                  memory={helpfulMemory}
                  known={helpfulMine?.get(note.id)}
                  product={product}
                  className={styles.voteLine}
                  end={button}
                  analytics={analytics}
                />
              ) : (
                <div className={clsx(styles.voteLine, styles.askLine)}>{button}</div>
              )}
              {panel}
            </>
          )}
        </AskOwner>
      )}
      {((helpful !== null && !votes) || repliesInActions) && (
        <div className={styles.actions}>
          {helpful !== null && !votes && (
            <HelpfulButton
              noteId={note.id}
              handle={author.handle}
              count={helpful}
              memory={helpfulMemory}
              known={helpfulMine?.get(note.id)}
              layout="text"
              className={styles.helpfulAction}
            />
          )}
          {repliesInActions && replies !== null && (
            <MaybeLink
              href={thread ? `${thread}#${REPLIES_ANCHOR}` : null}
              className={styles.replies}
            >
              {replyCountLine(replies)}
            </MaybeLink>
          )}
        </div>
      )}
    </div>
  )

  return (
    <article
      id={anchorId}
      className={clsx(styles.row, {
        [styles.rowLit]: highlighted,
        [styles.rowCondensed]: condensed,
      })}
      data-note-id={note.id}
    >
      {showProduct && (
        <div className={styles.thumbCol}>
          {hub ? (
            <Linked
              href={hub}
              className={styles.thumb}
              aria-label={`${product.name} owner reviews`}
            >
              {thumbImage}
            </Linked>
          ) : (
            <span className={styles.thumb}>{thumbImage}</span>
          )}
          {score && (
            <SgScoreRing
              id={note.id}
              score={score.score}
              text={score.text}
              label={score.label}
              className={styles.scoreRing}
            />
          )}
        </div>
      )}
      {owner ? (
        <div className={styles.ownWrap}>
          {body}
          <div className={styles.ownAside}>{owner.aside}</div>
        </div>
      ) : (
        body
      )}
    </article>
  )
}

export default FeedRow
